import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faEye, faTrash } from '@fortawesome/free-solid-svg-icons';
import { v4 as uuidv4 } from 'uuid';
import { useModal } from 'components/modals/ModalContext';
import { useGlobalContext } from 'GlobalContext';
import { useFetchData } from 'services/api/useFetchData';
import { saveData } from 'services/api/saveData';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import DeleteModal from 'components/modals/DeleteModal';
import Dropdown from 'components/forms/basefields/Dropdown';
import KnowledgeBaseEditor from 'components/forms/fields/KnowledgeBaseEditor';
import {
    KnowledgeBaseArticle,
    KnowledgeBaseArticleAccess,
    KnowledgeBaseArticleStatus,
    KnowledgeBaseFeatureOption,
    KnowledgeBaseSection,
    KNOWLEDGE_BASE_ADMIN_PATH,
    KNOWLEDGE_BASE_PUBLIC_PATH,
} from '../../knowledge_base/KnowledgeBaseTypes';
import '../../../style/scss/knowledge-base.scss';

// The editable fields of an article
interface ArticleFormState {
    subject: string;
    description: string;
    text: string;
    status: KnowledgeBaseArticleStatus;
    access: KnowledgeBaseArticleAccess;
    feature: string | null;
    section: number | null;
}

// A selectable option of the status and access dropdowns
interface ChoiceOption {
    value: string;
    name: string;
}

// The editor page of a knowledge base article, an intercom like white page
const KnowledgeBaseArticleEditorPage: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { id, groupId, sectionId } = useParams<{ id?: string, groupId?: string, sectionId?: string }>();
    const { initializeModal } = useModal();
    const { setFloatingAlert, setUnsavedChanges } = useGlobalContext();
    const viewKey = useMemo(() => uuidv4(), []);
    const isNew = !id;

    // The current article id, set after the first save of a new article
    const [articleId, setArticleId] = useState<number | null>(id ? Number(id) : null);
    const [currentGroupId, setCurrentGroupId] = useState<number | null>(groupId ? Number(groupId) : null);
    const [form, setForm] = useState<ArticleFormState>({
        subject: '', description: '', text: '', status: 'concept', access: 'public', feature: null, section: sectionId ? Number(sectionId) : null,
    });
    const [packages, setPackages] = useState<string[]>([]);
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState<Date | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const skipAutosaveRef = useRef(true);
    const savedFormRef = useRef<ArticleFormState | null>(null);
    const isDirtyRef = useRef(false);

    // Keep the latest context setters in refs since they change identity on every global render
    const setUnsavedChangesRef = useRef(setUnsavedChanges);
    const setFloatingAlertRef = useRef(setFloatingAlert);
    setUnsavedChangesRef.current = setUnsavedChanges;
    setFloatingAlertRef.current = setFloatingAlert;

    // Marks the form as dirty or clean and only touches the global state on a change
    const markDirty = useCallback((dirty: boolean) => {
        setIsDirty(dirty);
        if (isDirtyRef.current === dirty) return;
        isDirtyRef.current = dirty;
        setUnsavedChangesRef.current(viewKey, dirty);
    }, [viewKey]);

    // Fetch the existing article
    const { response: article } = useFetchData({ apiUrl: isNew ? '' : `get_knowledge_base_article_as_superuser/${id}` });

    // Fetch the sections of the group and the selectable features
    const sectionParams = useMemo(() => (currentGroupId ? { group: currentGroupId } : undefined), [currentGroupId]);
    const { response: sectionResponse } = useFetchData({ apiUrl: currentGroupId ? 'get_knowledge_base_section_list_as_superuser' : '', params: sectionParams });
    const { response: featureResponse } = useFetchData({ apiUrl: 'get_knowledge_base_feature_list_as_superuser' });

    // Fill the form with the fetched article
    useEffect(() => {
        if (!article) return;
        const fetched = article as KnowledgeBaseArticle;
        setForm({
            subject: fetched.subject ?? '',
            description: fetched.description ?? '',
            text: fetched.text ?? '',
            status: fetched.status,
            access: fetched.access,
            feature: fetched.feature,
            section: fetched.section,
        });
        setPackages(fetched.packages ?? []);
        setCurrentGroupId(fetched.group);
        skipAutosaveRef.current = true;
    }, [article]);

    // Updates a field of the form and marks the form as dirty
    const updateField = <K extends keyof ArticleFormState>(field: K, value: ArticleFormState[K]) => {
        setForm(current => ({ ...current, [field]: value }));
        markDirty(true);
    };

    // Saves the article, creates it on the first save, returns the id of the saved article or null when saving failed
    const saveArticle = useCallback(async (showAlert: boolean): Promise<number | null> => {
        if (isSaving) return null;
        setIsSaving(true);
        setErrors({});

        try {
            // Post a new article or patch the existing one, remember which state is saved
            savedFormRef.current = form;
            const data = { ...form, group: currentGroupId };
            const response = articleId
                ? await saveData({ apiUrl: `patch_knowledge_base_article_as_superuser/${articleId}`, method: 'patch', data })
                : await saveData({ apiUrl: 'post_knowledge_base_article_as_superuser', method: 'post', data });

            // Store the saved state
            const saved = response?.data as KnowledgeBaseArticle;
            setPackages(saved?.packages ?? []);
            setLastSaved(new Date());

            // Only clear the dirty state when nothing changed during the save
            setForm(current => {
                markDirty(current !== savedFormRef.current);
                return current;
            });
            if (showAlert) setFloatingAlertRef.current({ type: 'success', message: 'knowledge_base.article.saved_message' });

            // Replace the url of a new article with the url of the saved article
            if (!articleId && saved?.id) {
                setArticleId(saved.id);
                history.replace(`${KNOWLEDGE_BASE_ADMIN_PATH}/article/${saved.id}`);
            }
            return saved?.id ?? articleId;
        } catch (error: any) {
            // Show the field errors or a general alert
            const responseErrors = error?.response?.data;
            if (responseErrors && typeof responseErrors === 'object') {
                const fieldErrors: Record<string, string> = {};
                Object.entries(responseErrors).forEach(([field, message]) => { fieldErrors[field] = Array.isArray(message) ? String(message[0]) : String(message); });
                setErrors(fieldErrors);
            }
            setFloatingAlertRef.current({ type: 'danger', message: 'knowledge_base.general.save_failed_message' });
            return null;
        } finally {
            setIsSaving(false);
        }
    }, [articleId, currentGroupId, form, history, isSaving, markDirty]);

    // Opens the article in the public knowledge base, a concept is previewed there by the manager after saving the latest changes
    const openPreview = async () => {

        // Open the tab right away so the browser does not block it after the save
        const previewWindow = window.open('', '_blank');

        // Save the unsaved changes first so the preview shows the latest text
        const savedId = (isDirty && !isSaving) || !articleId ? await saveArticle(false) : articleId;
        if (!savedId) { previewWindow?.close(); return; }

        // Load the public article page in the new tab
        const url = `${KNOWLEDGE_BASE_PUBLIC_PATH}/artikel/${savedId}`;
        if (previewWindow) previewWindow.location.href = url;
        else window.open(url, '_blank');
    };

    // Autosave an existing article a moment after the last change
    useEffect(() => {
        if (skipAutosaveRef.current) { skipAutosaveRef.current = false; return; }
        if (!isDirty || !articleId) return;
        const timer = setTimeout(() => { saveArticle(false); }, 2000);
        return () => clearTimeout(timer);
    }, [form, isDirty, articleId, saveArticle]);

    // Clear the unsaved changes state when leaving the page
    useEffect(() => () => setUnsavedChangesRef.current(viewKey, false), [viewKey]);

    // Opens the delete confirmation of the article
    const handleDelete = () => {
        if (!articleId) { history.push(`${KNOWLEDGE_BASE_ADMIN_PATH}/group/${currentGroupId}`); return; }
        initializeModal(
            <DeleteModal onConfirmDelete={async () => {
                try {
                    await saveData({ apiUrl: `patch_knowledge_base_article_as_superuser/${articleId}`, method: 'patch', data: { deleted: true } });
                    markDirty(false);
                    history.push(`${KNOWLEDGE_BASE_ADMIN_PATH}/group/${currentGroupId}`);
                } catch (error) {
                    setFloatingAlert({ type: 'danger', message: 'knowledge_base.general.save_failed_message' });
                }
            }} />,
            { modalSize: 'extra-small' }
        );
    };

    // The options of the status and access dropdowns
    const statusOptions: ChoiceOption[] = [
        { value: 'concept', name: t('knowledge_base.article.status_concept') },
        { value: 'published', name: t('knowledge_base.article.status_published') },
    ];
    const accessOptions: ChoiceOption[] = [
        { value: 'public', name: t('knowledge_base.article.access_public') },
        { value: 'restricted', name: t('knowledge_base.article.access_restricted') },
    ];

    // The feature options with their translated names, main features first
    const featureOptions = useMemo(() => {
        const features: KnowledgeBaseFeatureOption[] = featureResponse?.results ?? [];
        return features.map(feature => ({
            value: feature.feature,
            name: t(feature.feature_translation),
            group: t(`knowledge_base.article.feature_type_${feature.feature_type}`),
        }));
    }, [featureResponse, t]);

    // The section options of the group
    const sectionOptions = useMemo(() => ((sectionResponse?.results ?? []) as KnowledgeBaseSection[]).map(section => ({ id: section.id, name: section.name })), [sectionResponse]);

    return (
        <div className='kb-editor-page'>
            <div className='kb-editor-topbar'>
                <div className='kb-back-link' onClick={() => history.push(`${KNOWLEDGE_BASE_ADMIN_PATH}/group/${currentGroupId}`)}>
                    <FontAwesomeIcon icon={faAngleLeft} /> {t('knowledge_base.general.back_to_group')}
                </div>
                <div className='kb-editor-topbar-status'>
                    {isSaving && <span className='kb-save-indicator'>{t('knowledge_base.article.saving')}</span>}
                    {!isSaving && isDirty && <span className='kb-save-indicator'>{t('knowledge_base.article.unsaved')}</span>}
                    {!isSaving && !isDirty && lastSaved && <span className='kb-save-indicator'>{t('knowledge_base.article.saved_at', { time: lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) })}</span>}
                </div>
                <div className='kb-admin-toolbar-buttons'>
                    <SecondaryButton label={form.status === 'published' ? 'knowledge_base.article.view_public_button' : 'knowledge_base.article.preview_button'} size='small'
                        icon={<FontAwesomeIcon icon={faEye} />} onClick={openPreview} />
                    <SecondaryButton label='general.delete' size='small' icon={<FontAwesomeIcon icon={faTrash} />} onClick={handleDelete} />
                    <PrimaryButton label='general.save' size='small' loading={isSaving} onClick={() => saveArticle(true)} />
                </div>
            </div>

            <div className='kb-editor-sheet'>
                <div className='kb-editor-settings'>
                    <div className='kb-editor-setting'>
                        <label>{t('knowledge_base.article.status_label')}</label>
                        <Dropdown<ChoiceOption>
                            name='status' options={statusOptions} backendField='value' optionFormat='name' showSearch={false}
                            selectedOption={statusOptions.find(option => option.value === form.status)}
                            selectionFormat={(option) => option.name}
                            onChange={(value) => updateField('status', value as KnowledgeBaseArticleStatus)} />
                    </div>
                    <div className='kb-editor-setting'>
                        <label>{t('knowledge_base.article.access_label')}</label>
                        <Dropdown<ChoiceOption>
                            name='access' options={accessOptions} backendField='value' optionFormat='name' showSearch={false}
                            selectedOption={accessOptions.find(option => option.value === form.access)}
                            selectionFormat={(option) => option.name}
                            onChange={(value) => updateField('access', value as KnowledgeBaseArticleAccess)} />
                    </div>
                    <div className='kb-editor-setting'>
                        <label>{t('knowledge_base.article.section_label')}</label>
                        <Dropdown<{ id: number, name: string }>
                            name='section' options={sectionOptions} optionFormat='name' showSearch={false} allowNoneOption={true}
                            noneOptionLabel='knowledge_base.section.without_section'
                            selectedOption={sectionOptions.find(option => option.id === form.section) ?? null}
                            selectionFormat={(option) => option.name}
                            onChange={(value) => updateField('section', value === 'none' ? null : Number(value))} />
                    </div>
                    <div className='kb-editor-setting'>
                        <label>{t('knowledge_base.article.feature_label')}</label>
                        <Dropdown<{ value: string, name: string, group: string }>
                            name='feature' options={featureOptions} backendField='value' optionFormat='name' groupByField='group' sortOptions={true} showSearch={true} allowNoneOption={true}
                            noneOptionLabel='knowledge_base.article.no_feature'
                            selectedOption={featureOptions.find(option => option.value === form.feature) ?? null}
                            selectionFormat={(option) => option.name}
                            onChange={(value) => updateField('feature', value === 'none' ? null : value)} />
                    </div>
                </div>

                {packages.length > 0 && (
                    <div className='kb-package-tags'>
                        <span className='kb-package-tags-label'>{t('knowledge_base.article.packages_label')}</span>
                        {packages.map(name => <span key={name} className='kb-package-tag'>{name}</span>)}
                    </div>
                )}

                <input
                    type='text'
                    className={`kb-editor-title ${errors.subject ? 'is-invalid' : ''}`}
                    placeholder={t('knowledge_base.article.subject_placeholder')}
                    value={form.subject}
                    onChange={(event) => updateField('subject', event.target.value)} />
                {errors.subject && <div className='kb-field-error'>{errors.subject}</div>}
                <textarea
                    className='kb-editor-description'
                    placeholder={t('knowledge_base.article.description_placeholder')}
                    value={form.description}
                    rows={2}
                    onChange={(event) => updateField('description', event.target.value)} />

                <KnowledgeBaseEditor
                    value={form.text}
                    onChange={(value) => updateField('text', value)}
                    articleId={articleId}
                    placeholder='knowledge_base.editor.placeholder'
                    onUploadError={() => setFloatingAlert({ type: 'danger', message: 'knowledge_base.editor.image_upload_failed' })} />
            </div>
        </div>
    );
};

export default KnowledgeBaseArticleEditorPage;
